import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLazyQuery, useQuery, useMutation } from '@apollo/client';
import {
  GET_RECIPES,
  GET_RECIPE,
  ADD_RECIPE,
  UPDATE_RECIPE,
  DELETE_RECIPE,
  IMPORT_RECIPE_FROM_URL,
  GET_RECIPE_SHARES,
  SHARE_RECIPE,
  REVOKE_RECIPE_SHARE,
  START_COOKING_SESSION,
  UPDATE_COOKING_SESSION,
  FINISH_COOKING_SESSION,
  DISCARD_COOKING_SESSION,
  LOG_PAST_COOKING_SESSION,
  GET_RECIPE_COOKING_HISTORY,
} from './operations';
import CookingForm from './CookingForm';
import ImportModal from './ImportModal';
import ShareRecipeModal from './ShareRecipeModal';
import CookingSessionModal from './CookingSessionModal';
import PremiumUpsellModal from '../../components/PremiumUpsellModal';
import ModuleListSkeleton from '../../components/common/ModuleListSkeleton';
import DetailActionMenu from '../../components/common/DetailActionMenu';
import EntryImage from '../../components/common/EntryImage';
import HobbyEmptyState from '../../components/common/HobbyEmptyState';
import RatingReadout from '../../components/common/RatingReadout';
import { GET_MY_SUBSCRIPTION_STATUS } from '../../views/account/operations';
import { getPublicErrorMessage, isLimitReachedError } from '../../utils/errors';
import { canCreateEntries } from '../../utils/subscriptionLimits';
import { fileToBase64 } from '../../components/EntryPhotoPicker';
import { REMOVE_ENTRY_COVER_PHOTO, UPLOAD_ENTRY_COVER_PHOTO } from '../entryPhotos/operations';
import { normalizeRecipeQuantity } from '../../validation/hobbies';
import { cookingStrings, moduleCommonStrings } from '../../content/hobbyModules';
import { shareHighlightStrings } from '../../content/shareHighlights';
import { buildShareHighlight, openShareHighlight } from '../shareHighlights/shareHighlight';
 
const RECIPES_PAGE_SIZE = 20;

/**
 * Performs the access level for operation.
 *
 * @param {*} item - item value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
const accessLevelFor = (item) => item?.accessLevel || 'owner';
/**
 * Determines whether edit access.
 *
 * @param {*} item - item value.
 * @returns {boolean} Whether the requested condition is satisfied.
 */
const canEditAccess = (item) => ['owner', 'edit'].includes(accessLevelFor(item));
/**
 * Determines whether owner access.
 *
 * @param {*} item - item value.
 * @returns {boolean} Whether the requested condition is satisfied.
 */
const isOwnerAccess = (item) => accessLevelFor(item) === 'owner';
/**
 * Performs the shared access label operation.
 *
 * @param {*} accessLevel - access level value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
const sharedAccessLabel = (accessLevel) => moduleCommonStrings.sharing.accessLine(accessLevel);
/**
 * Performs the shared by label operation.
 *
 * @param {string} name - name value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
const sharedByLabel = (name) => moduleCommonStrings.sharing.sharedBy(name);

const defaultMeta = {
  description: '',
  sourceUrl: '',
  recipeBook: 'Unsorted',
  category: '',
  categories: [],
  mealTypes: [],
  status: 'Want to try',
  cuisine: '',
  dietary: '',
  tags: '',
  difficulty: '',
  prepTime: '',
  cookTime: '',
  totalTime: '',
  servings: '',
  yield: '',
  rating: '',
  favorite: false,
  notes: '',
  calories: '',
  protein: '',
  instructions: '',
  coverPhotoUrl: '',
  coverPhotoUpdatedAt: '',
};

/**
 * Performs the ingredients to text operation.
 *
 * @param {*} ingredients - ingredients value.
 * @returns {*} The derived operation result.
 */
function ingredientsToText(ingredients) {
  if (!Array.isArray(ingredients)) return '';
  return ingredients
    .map((ingredient) => {
      if (typeof ingredient === 'string') return ingredient;
      return [ingredient.quantity, ingredient.unit, ingredient.item, ingredient.note && `(${ingredient.note})`]
        .filter(Boolean)
        .join(' ')
        .trim();
    })
    .filter(Boolean)
    .join('\n');
}

/**
 * Normalizes ingredients.
 *
 * @param {*} ingredients - ingredients value.
 * @returns {*} The derived operation result.
 */
function cleanIngredients(ingredients) {
  return ingredients
    .filter((ingredient) => ingredient.item?.trim())
    .map((ingredient) => ({
      id: ingredient.id || undefined,
      quantity: normalizeRecipeQuantity(ingredient.quantity),
      unit: ingredient.unit?.trim() || '',
      item: ingredient.item.trim(),
      note: ingredient.note?.trim() || '',
    }));
}

/**
 * Normalizes instructions.
 *
 * @param {*} instructions - instructions value.
 * @returns {*} The derived operation result.
 */
function cleanInstructions(instructions) {
  return instructions
    .filter((step) => step.text?.trim())
    .map((step) => ({ id: step.id || undefined, text: step.text.trim() }));
}

/**
 * Performs the split csv operation.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function splitCsv(value) {
  if (Array.isArray(value)) return value;
  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

/**
 * Normalizes instructions.
 *
 * @param {*} instructions - instructions value.
 * @returns {*} The derived operation result.
 */
function normalizeInstructions(instructions) {
  if (Array.isArray(instructions)) return instructions;
  return String(instructions || '')
    .split('\n')
    .map((line) => line.replace(/^\d+[\).]\s*/, '').trim())
    .filter(Boolean)
    .map((text) => ({ text }));
}

/**
 * Performs the minutes value operation.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function minutesValue(value) {
  const match = String(value || '').match(/\d+(\.\d+)?/);
  return match ? match[0] : '';
}

/**
 * Normalizes recipe.
 *
 * @param {*} recipe - recipe value.
 * @returns {*} The derived operation result.
 */
function parseRecipe(recipe) {
  const normalizedIngredients = Array.isArray(recipe.ingredients)
    ? recipe.ingredients.map((ingredient) => (
      typeof ingredient === 'string'
        ? { quantity: '', unit: '', item: ingredient, note: '' }
        : ingredient
    ))
    : [];
  const normalizedInstructions = normalizeInstructions(recipe.instructions);

  return {
    ...defaultMeta,
    ...recipe,
    recipeBook: recipe.recipeBook || defaultMeta.recipeBook,
    categories: Array.isArray(recipe.categories) && recipe.categories.length ? recipe.categories : [recipe.category || defaultMeta.category].filter(Boolean),
    mealTypes: Array.isArray(recipe.mealTypes) ? recipe.mealTypes : [],
    status: recipe.status || defaultMeta.status,
    category: recipe.category || recipe.categories?.[0] || defaultMeta.category,
    ingredients: normalizedIngredients,
    instructions: normalizedInstructions,
    ingredientsText: ingredientsToText(normalizedIngredients),
    tagList: recipe.tags || [],
    dietaryList: recipe.dietary || [],
  };
}

/**
 * Performs the unique values operation.
 *
 * @param {*} recipes - recipes value.
 * @param {string} field - Field associated with the operation.
 * @returns {*} The derived operation result.
 */
function uniqueValues(recipes, field) {
  return Array.from(new Set(recipes.flatMap((recipe) => {
    const value = recipe[field];
    return Array.isArray(value) ? value : [value];
  }).filter(Boolean))).sort();
}

/**
 * Performs the display cooked date operation.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function displayCookedDate(value) {
  const raw = String(value || '').slice(0, 10);
  if (!raw) return '';
  const date = new Date(`${raw}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? raw
    : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Owns Cooking list/detail routing, recipe mutations, imports, sharing, and cook history. */
export default function CookingModule({
  isAdding,
  setIsAdding,
  theme,
  modulePath = '',
  onClose,
  onRegisterCloseGuard,
  onHeaderActionsChange,
  onHeaderTitleChange,
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const targetItemId = searchParams.get('itemId');
  const openedTargetRef = useRef(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [scrapedData, setScrapedData] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [recipePendingDelete, setRecipePendingDelete] = useState(null);
  const [sharingRecipe, setSharingRecipe] = useState(null);
  const [activeCookingSession, setActiveCookingSession] = useState(null);
  const [recentCookingEntries, setRecentCookingEntries] = useState([]);
  const [showLogPastCook, setShowLogPastCook] = useState(false);
  const [showCookingHistory, setShowCookingHistory] = useState(false);
  const [historyRecipeId, setHistoryRecipeId] = useState('');
  const [shareSuccess, setShareSuccess] = useState('');
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [isLoadingRouteRecipe, setIsLoadingRouteRecipe] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [search, setSearch] = useState('');
  const [activeBook, setActiveBook] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [activeCuisine, setActiveCuisine] = useState('');
  const [activeMealType, setActiveMealType] = useState('');
  const [activeDiet, setActiveDiet] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [showPremiumUpsell, setShowPremiumUpsell] = useState(false);
  const routeParts = useMemo(
    () => String(modulePath || '').split('/').map((part) => decodeURIComponent(part)).filter(Boolean),
    [modulePath]
  );
  const isCreateRoute = routeParts[0] === 'new';
  const routeRecipeId = routeParts[0] && routeParts[0] !== 'new' ? routeParts[0] : '';
  const isEditRoute = Boolean(routeRecipeId && routeParts[1] === 'edit');
  const isAddFlow = isAdding || isCreateRoute;
  const subscriptionRefetch = [{ query: GET_MY_SUBSCRIPTION_STATUS }];
  const { data: subscriptionData } = useQuery(GET_MY_SUBSCRIPTION_STATUS, {
    fetchPolicy: 'cache-and-network',
  });
  const subscription = subscriptionData?.getMySubscriptionStatus;

  const { loading, error, data, fetchMore, refetch: refetchRecipes } = useQuery(GET_RECIPES, {
    variables: { first: RECIPES_PAGE_SIZE, after: null },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });
  const parsedRecipes = useMemo(
    () => (data?.getRecipesListPage?.nodes || []).map(parseRecipe),
    [data]
  );
  const recipesPageInfo = data?.getRecipesListPage?.pageInfo || {};
  const [loadRecipeDetail] = useLazyQuery(GET_RECIPE, { fetchPolicy: 'network-only' });
  const { data: shareData, refetch: refetchShares } = useQuery(GET_RECIPE_SHARES, {
    variables: { recipeId: sharingRecipe?.id || '' },
    skip: !sharingRecipe,
    fetchPolicy: 'network-only',
  });
  const [loadCookingHistory, { data: cookingHistoryData, loading: cookingHistoryLoading, fetchMore: fetchMoreCookingHistory }] = useLazyQuery(
    GET_RECIPE_COOKING_HISTORY,
    { fetchPolicy: 'cache-and-network' }
  );
  const cookingHistory = useMemo(() => {
    const serverEntries = historyRecipeId === selectedRecipe?.id
      ? cookingHistoryData?.getRecipeCookingHistory?.nodes || []
      : [];
    const entries = [...recentCookingEntries, ...serverEntries];
    return entries.filter((entry, index) => entries.findIndex((candidate) => candidate.id === entry.id) === index);
  }, [cookingHistoryData, historyRecipeId, recentCookingEntries, selectedRecipe?.id]);

  useEffect(() => {
    setRecentCookingEntries([]);
    setShowCookingHistory(false);
    setHistoryRecipeId('');
  }, [selectedRecipe?.id]);

  useEffect(() => {
    if (!showCookingHistory || !selectedRecipe?.id) return;
    setHistoryRecipeId(selectedRecipe.id);
    loadCookingHistory({ variables: { recipeId: selectedRecipe.id, first: 10, after: null } });
  }, [loadCookingHistory, selectedRecipe?.id, showCookingHistory]);

  const recipeBooks = useMemo(() => uniqueValues(parsedRecipes, 'recipeBook'), [parsedRecipes]);
  const categories = useMemo(() => uniqueValues(parsedRecipes, 'categories'), [parsedRecipes]);
  const mealTypes = useMemo(() => uniqueValues(parsedRecipes, 'mealTypes'), [parsedRecipes]);
  const cuisines = useMemo(() => uniqueValues(parsedRecipes, 'cuisine'), [parsedRecipes]);
  const diets = useMemo(
    () => Array.from(new Set(parsedRecipes.flatMap((recipe) => recipe.dietaryList))).sort(),
    [parsedRecipes]
  );

  const filteredRecipes = useMemo(() => {
    const query = search.trim().toLowerCase();

    return parsedRecipes.filter((recipe) => {
      const searchable = [
        recipe.name,
        recipe.description,
        recipe.recipeBook,
        recipe.categories.join(' '),
        recipe.mealTypes.join(' '),
        recipe.status,
        recipe.cuisine,
        recipe.tagList.join(' '),
        recipe.dietaryList.join(' '),
        recipe.ingredientsText,
      ].join(' ').toLowerCase();

      return (
        (!query || searchable.includes(query)) &&
        (!activeBook || recipe.recipeBook === activeBook) &&
        (!activeCategory || recipe.categories.includes(activeCategory)) &&
        (!activeMealType || recipe.mealTypes.includes(activeMealType)) &&
        (!activeCuisine || recipe.cuisine === activeCuisine) &&
        (!activeDiet || recipe.dietaryList.includes(activeDiet)) &&
        (!favoritesOnly || recipe.favorite)
      );
    });
  }, [activeBook, activeCategory, activeCuisine, activeDiet, activeMealType, favoritesOnly, parsedRecipes, search]);

  const [addRecipe, { loading: isSaving }] = useMutation(ADD_RECIPE, {
    refetchQueries: [{ query: GET_RECIPES, variables: { first: RECIPES_PAGE_SIZE, after: null } }, ...subscriptionRefetch],
    onCompleted: () => {
      setScrapedData(null);
      setHasFormChanges(false);
    },
  });

  const [updateRecipe, { loading: isUpdating }] = useMutation(UPDATE_RECIPE, {
    refetchQueries: [{ query: GET_RECIPES, variables: { first: RECIPES_PAGE_SIZE, after: null } }],
    onCompleted: (result) => {
      setEditingRecipe(null);
      setHasFormChanges(false);
      setSelectedRecipe(parseRecipe(result.updateRecipe));
    },
  });

  const [deleteRecipe, { loading: isDeleting }] = useMutation(DELETE_RECIPE, {
    refetchQueries: [{ query: GET_RECIPES, variables: { first: RECIPES_PAGE_SIZE, after: null } }, ...subscriptionRefetch],
    onCompleted: () => {
      setRecipePendingDelete(null);
      setSelectedRecipe(null);
      navigate('/hobby/cooking');
    },
  });

  const [importRecipe, { loading: isImporting }] = useMutation(IMPORT_RECIPE_FROM_URL, {
    onCompleted: (result) => {
      const imported = result.importRecipe;
      setScrapedData({
        title: imported.name || '',
        description: imported.description || '',
        ingredients: imported.ingredients || [],
        instructions: imported.instructions || [],
        sourceUrl: imported.sourceUrl || importUrl,
        mealTypes: imported.mealTypes || [],
        status: imported.status || 'Want to try',
        recipeBook: recipeBooks[0] || imported.recipeBook || cookingStrings.form.defaultRecipeBook,
        category: imported.category || 'Dinner',
        categories: imported.categories?.length ? imported.categories : [imported.category || 'Dinner'],
        cuisine: imported.cuisine || '',
        dietary: imported.dietary || [],
        tags: imported.tags || ['imported'],
        difficulty: imported.difficulty || 'Easy',
        prepTime: minutesValue(imported.prepTime),
        cookTime: minutesValue(imported.cookTime),
        totalTime: minutesValue(imported.totalTime),
        servings: imported.servings || '',
        rating: imported.rating || '',
        favorite: imported.favorite || false,
        notes: imported.notes || '',
        calories: imported.calories || '',
        protein: imported.protein || '',
      });
      setShowImportModal(false);
      setIsAdding(false);
      navigate('/hobby/cooking/new');
    },
    onError: (err) => {
      if (isLimitReachedError(err)) {
        setShowPremiumUpsell(true);
        return;
      }
      alert(`Import failed: ${getPublicErrorMessage(err)}`);
    },
  });

  const [shareRecipe, { loading: isSharing, error: shareError }] = useMutation(SHARE_RECIPE, {
    onCompleted: async (result) => {
      const share = result.shareRecipe;
      setShareSuccess(share.status === 'pending'
        ? `Invite pending for ${share.granteeEmail}.`
        : `${share.granteeDisplayName || share.granteeEmail} now has ${share.permission === 'edit' ? 'edit' : 'view'} access.`);
      await refetchShares?.();
    },
  });

  const [revokeRecipeShare, { loading: isRevoking }] = useMutation(REVOKE_RECIPE_SHARE, {
    onCompleted: async () => {
      setShareSuccess('Access removed.');
      await refetchShares?.();
    },
  });
  const [uploadEntryCoverPhoto, { loading: isUploadingPhoto }] = useMutation(UPLOAD_ENTRY_COVER_PHOTO);
  const [removeEntryCoverPhoto, { loading: isRemovingPhoto }] = useMutation(REMOVE_ENTRY_COVER_PHOTO);
  const [startCookingSession] = useMutation(START_COOKING_SESSION);
  const [updateCookingSession] = useMutation(UPDATE_COOKING_SESSION);
  const [finishCookingSession] = useMutation(FINISH_COOKING_SESSION);
  const [discardCookingSession] = useMutation(DISCARD_COOKING_SESSION);
  const [logPastCookingSession] = useMutation(LOG_PAST_COOKING_SESSION);

  /**
   * Performs the refresh recipe after cooking operation.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const refreshRecipeAfterCooking = async () => {
    if (!selectedRecipe?.id) return;
    const detail = await fetchRecipeDetail(selectedRecipe);
    setSelectedRecipe(detail);
    await refetchRecipes();
  };

  /**
   * Handles load more cooking history.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleLoadMoreCookingHistory = async () => {
    const pageInfo = cookingHistoryData?.getRecipeCookingHistory?.pageInfo;
    if (!selectedRecipe?.id || !pageInfo?.hasNextPage || !pageInfo.endCursor) return;
    await fetchMoreCookingHistory({
      variables: { recipeId: selectedRecipe.id, first: 10, after: pageInfo.endCursor },
      updateQuery: (previous, { fetchMoreResult }) => {
        const previousConnection = previous?.getRecipeCookingHistory;
        const nextConnection = fetchMoreResult?.getRecipeCookingHistory;
        if (!nextConnection) return previous;
        const existingIds = new Set((previousConnection?.nodes || []).map((entry) => entry.id));
        return {
          getRecipeCookingHistory: {
            ...nextConnection,
            nodes: [...(previousConnection?.nodes || []), ...nextConnection.nodes.filter((entry) => !existingIds.has(entry.id))],
          },
        };
      },
    });
  };

  /**
   * Handles start cooking.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleStartCooking = async () => {
    const result = await startCookingSession({ variables: { recipeId: selectedRecipe.id } });
    setActiveCookingSession(result.data.startCookingSession);
  };

  /**
   * Handles save cooking progress.
   *
   * @param {*} session - session value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSaveCookingProgress = async (session) => {
    const result = await updateCookingSession({
      variables: {
        id: session.id,
        input: {
          servingsMultiplier: Number(session.servingsMultiplier || 1),
          checkedIngredientIds: session.checkedIngredientIds || [],
          completedInstructionIds: session.completedInstructionIds || [],
          currentInstructionId: session.currentInstructionId || null,
          notes: session.notes || '',
        },
      },
    });
    const saved = result.data.updateCookingSession;
    setActiveCookingSession(saved);
    return saved;
  };

  /**
   * Handles finish cooking.
   *
   * @param {object} options - Behavioral options for the operation.
   * @param {*} options.cookedDate - cooked date value.
   * @param {*} options.notes - notes value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleFinishCooking = async ({ cookedDate, notes }) => {
    const result = await finishCookingSession({ variables: { id: activeCookingSession.id, input: { cookedDate, notes } } });
    const completed = result.data?.finishCookingSession;
    if (completed) setRecentCookingEntries((entries) => [completed, ...entries.filter((entry) => entry.id !== completed.id)]);
    setActiveCookingSession(null);
    await refreshRecipeAfterCooking();
  };

  /**
   * Handles discard cooking.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleDiscardCooking = async () => {
    await discardCookingSession({ variables: { id: activeCookingSession.id } });
    setActiveCookingSession(null);
    await refreshRecipeAfterCooking();
  };

  /**
   * Handles log past cook.
   *
   * @param {*} event - Browser or React event that triggered the action.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleLogPastCook = async (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const result = await logPastCookingSession({ variables: { recipeId: selectedRecipe.id, input: {
      cookedDate: form.get('cookedDate'), notes: form.get('notes') || '',
    } } });
    const completed = result.data?.logPastCookingSession;
    if (completed) setRecentCookingEntries((entries) => [completed, ...entries.filter((entry) => entry.id !== completed.id)]);
    setShowLogPastCook(false);
    await refreshRecipeAfterCooking();
  };

  /**
   * Performs recipe photo.
   *
   * @param {*} formData - form data value.
   * @param {string} recipeId - recipe id value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const syncRecipePhoto = async (formData, recipeId) => {
    if (!recipeId) return;
    const refetchQueries = [{ query: GET_RECIPES, variables: { first: RECIPES_PAGE_SIZE, after: null } }];

    if (formData.coverPhotoFile) {
      await uploadEntryCoverPhoto({
        variables: {
          input: {
            hobbyId: 'cooking',
            entryId: recipeId,
            contentType: formData.coverPhotoFile.type || 'image/jpeg',
            base64Data: await fileToBase64(formData.coverPhotoFile),
          },
        },
        refetchQueries,
        awaitRefetchQueries: true,
      });
      return;
    }

    if (formData.shouldRemoveCoverPhoto) {
      await removeEntryCoverPhoto({
        variables: { hobbyId: 'cooking', entryId: recipeId },
        refetchQueries,
        awaitRefetchQueries: true,
      });
    }
  };

  /**
   * Handles save.
   *
   * @param {*} formData - form data value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSave = async (formData) => {
    const input = {
      name: formData.title,
      description: formData.description,
      sourceUrl: formData.sourceUrl,
      recipeBook: formData.recipeBook,
      category: formData.categories?.[0] || formData.category,
      categories: formData.categories || [],
      mealTypes: formData.mealTypes || [],
      status: formData.status,
      cuisine: formData.cuisine,
      dietary: splitCsv(formData.dietary),
      tags: splitCsv(formData.tags),
      difficulty: formData.difficulty,
      prepTime: formData.prepTime,
      cookTime: formData.cookTime,
      totalTime: formData.totalTime,
      servings: formData.servings,
      rating: formData.rating,
      favorite: formData.favorite,
      ingredients: cleanIngredients(formData.ingredients),
      instructions: cleanInstructions(formData.instructions),
      notes: formData.notes,
      calories: formData.calories,
      protein: formData.protein,
    };

    try {
      if (formData.id) {
        await updateRecipe({ variables: { id: formData.id, input } });
        await syncRecipePhoto(formData, formData.id);
        const detail = await fetchRecipeDetail({ id: formData.id });
        setSelectedRecipe(detail);
        navigate(`/hobby/cooking/${encodeURIComponent(formData.id)}`);
        return;
      }

      const result = await addRecipe({ variables: { input } });
      await syncRecipePhoto(formData, result.data?.addRecipe?.id);
      navigate('/hobby/cooking');
      onClose();
    } catch (error) {
      if (isLimitReachedError(error)) {
        setShowPremiumUpsell(true);
        return;
      }
      alert(getPublicErrorMessage(error));
    }
  };

  const closeForm = useCallback(() => {
    const editingRecipeId = editingRecipe?.id;
    setScrapedData(null);
    setEditingRecipe(null);
    setHasFormChanges(false);
    navigate(editingRecipeId ? `/hobby/cooking/${encodeURIComponent(editingRecipeId)}` : '/hobby/cooking');
    onClose();
  }, [editingRecipe?.id, navigate, onClose]);

  const handleCancelForm = useCallback(({ confirmDiscard } = {}) => {
    if (confirmDiscard && hasFormChanges) {
      const shouldDiscard = window.confirm('Discard your unsaved recipe changes?');
      if (!shouldDiscard) return;
    }

    closeForm();
  }, [closeForm, hasFormChanges]);

  /**
   * Handles confirm delete.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleConfirmDelete = async () => {
    if (!recipePendingDelete) return;
    await deleteRecipe({ variables: { id: recipePendingDelete.id } });
  };

  /**
   * Handles share recipe.
   *
   * @param {object} options - Behavioral options for the operation.
   * @param {*} options.granteeEmail - grantee email value.
   * @param {*} options.permission - permission value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleShareRecipe = async ({ granteeEmail, permission }) => {
    if (!sharingRecipe) return;
    setShareSuccess('');
    await shareRecipe({ variables: { input: { recipeId: sharingRecipe.id, granteeEmail, permission } } });
  };

  /**
   * Handles revoke share.
   *
   * @param {string} id - Stable identifier of the target resource.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleRevokeShare = async (id) => {
    setShareSuccess('');
    await revokeRecipeShare({ variables: { id } });
  };

  /**
   * Handles load more.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleLoadMore = async () => {
    if (!recipesPageInfo.hasNextPage || !recipesPageInfo.endCursor) return;
    await fetchMore({
      variables: {
        first: RECIPES_PAGE_SIZE,
        after: recipesPageInfo.endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.getRecipesListPage) return previousResult;
        return {
          getRecipesListPage: {
            ...fetchMoreResult.getRecipesListPage,
            nodes: [
              ...(previousResult.getRecipesListPage?.nodes || []),
              ...(fetchMoreResult.getRecipesListPage.nodes || []),
            ],
          },
        };
      },
    });
  };

  /**
   * Determines whether start create flow.
   *
   * @returns {boolean} Whether the requested condition is satisfied.
   */
  const canStartCreateFlow = () => {
    if (canCreateEntries(subscription, 'cooking')) return true;
    setShowPremiumUpsell(true);
    return false;
  };

  /**
   * Handles start add.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleStartAdd = () => {
    if (!canStartCreateFlow()) return;
    setScrapedData(null);
    setIsAdding(false);
    navigate('/hobby/cooking/new');
  };

  /**
   * Handles start import.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleStartImport = () => {
    if (!canStartCreateFlow()) return;
    setShowImportModal(true);
  };

  /**
   * Renders recipe action menu.
   *
   * @param {*} recipe - recipe value.
   * @returns {JSX.Element} The derived operation result.
   */
  const renderRecipeActionMenu = (recipe) => {
    if (!recipe) return null;
    const canEditRecipe = canEditAccess(recipe);
    const isRecipeOwner = isOwnerAccess(recipe);

    return (
      <DetailActionMenu
        label="Recipe actions"
        primaryAction={canEditRecipe ? { label: 'Edit', onSelect: () => handleEditRecipe(recipe) } : null}
        actions={[
          isRecipeOwner && { label: shareHighlightStrings.action, onSelect: () => openShareHighlight(buildShareHighlight('cooking', recipe)) },
          isRecipeOwner && {
            label: shareHighlightStrings.manageAccess,
            onSelect: () => {
              setSharingRecipe(recipe);
              setShareSuccess('');
            },
          },
          isRecipeOwner && { label: 'Delete recipe', variant: 'danger', onSelect: () => setRecipePendingDelete(recipe) },
        ]}
      />
    );
  };

  useEffect(() => {
    if (isEditRoute || editingRecipe) {
      onHeaderTitleChange?.('Edit recipe');
    } else if (isCreateRoute || isAdding) {
      onHeaderTitleChange?.('Add recipe');
    } else if (routeRecipeId || selectedRecipe) {
      onHeaderTitleChange?.(selectedRecipe?.name || 'Recipe');
    } else {
      onHeaderTitleChange?.('');
    }

    return () => onHeaderTitleChange?.('');
  }, [editingRecipe, isAdding, isCreateRoute, isEditRoute, onHeaderTitleChange, routeRecipeId, selectedRecipe]);

  useEffect(() => {
    if (selectedRecipe && !isAddFlow && !editingRecipe) {
      onHeaderActionsChange?.(renderRecipeActionMenu(selectedRecipe));
      return () => onHeaderActionsChange?.(null);
    }

    if (isAddFlow || editingRecipe || routeRecipeId) {
      onHeaderActionsChange?.(null);
      return () => onHeaderActionsChange?.(null);
    }

    onHeaderActionsChange?.(
      <div className="recipe-actions">
        <button className="btn-secondary" type="button" onClick={handleStartImport}>
          {cookingStrings.actions.importFromUrl}
        </button>
        <button className="btn-primary" type="button" onClick={handleStartAdd}>
          Add recipe
        </button>
      </div>
    );
    return () => onHeaderActionsChange?.(null);
  }, [editingRecipe, isAddFlow, onHeaderActionsChange, routeRecipeId, selectedRecipe, subscription]);

  /**
   * Renders premium upsell.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const renderPremiumUpsell = () => showPremiumUpsell && (
    <PremiumUpsellModal
      hobbyId="cooking"
      subscription={subscription}
      onClose={() => setShowPremiumUpsell(false)}
      onOpenAccount={() => {
        setShowPremiumUpsell(false);
        window.location.assign('/account');
      }}
    />
  );

  const fetchRecipeDetail = useCallback(async (recipe) => {
    const result = await loadRecipeDetail({ variables: { id: recipe.id } });
    return parseRecipe(result.data?.getRecipe || recipe);
  }, [loadRecipeDetail]);

  /**
   * Handles select recipe.
   *
   * @param {*} recipe - recipe value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleSelectRecipe = (recipe) => {
    setSelectedRecipe(recipe);
    navigate(`/hobby/cooking/${encodeURIComponent(recipe.id)}`);
  };

  useEffect(() => {
    if (isCreateRoute) {
      setSelectedRecipe(null);
      setEditingRecipe(null);
      setIsAdding(true);
      setIsLoadingRouteRecipe(false);
      return;
    }

    if (!routeRecipeId) {
      setSelectedRecipe(null);
      setEditingRecipe(null);
      setScrapedData(null);
      setIsAdding(false);
      setIsLoadingRouteRecipe(false);
      return;
    }

    setIsAdding(false);
    setIsLoadingRouteRecipe(true);
    const recipe = parsedRecipes.find((item) => item.id === routeRecipeId) || { id: routeRecipeId };
    fetchRecipeDetail(recipe)
      .then((detail) => {
        if (isEditRoute) {
          if (!canEditAccess(detail)) {
            setEditingRecipe(null);
            setSelectedRecipe(detail);
            navigate(`/hobby/cooking/${encodeURIComponent(detail.id)}`, { replace: true });
            setIsLoadingRouteRecipe(false);
            return;
          }
          setSelectedRecipe(null);
          setEditingRecipe({
            ...detail,
            title: detail.name,
            dietary: detail.dietaryList,
            tags: detail.tagList,
          });
          setIsLoadingRouteRecipe(false);
          return;
        }
        setEditingRecipe(null);
        setSelectedRecipe(detail);
        setIsLoadingRouteRecipe(false);
      })
      .catch(() => {
        setIsLoadingRouteRecipe(false);
        navigate('/hobby/cooking', { replace: true });
      });
  }, [fetchRecipeDetail, isCreateRoute, isEditRoute, navigate, parsedRecipes, routeRecipeId, setIsAdding]);

  useEffect(() => {
    if (!targetItemId || openedTargetRef.current === targetItemId) return;
    openedTargetRef.current = targetItemId;
    navigate(`/hobby/cooking/${encodeURIComponent(targetItemId)}`, { replace: true });
  }, [navigate, targetItemId]);

  /**
   * Handles edit recipe.
   *
   * @param {*} recipe - recipe value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleEditRecipe = async (recipe) => {
    if (!canEditAccess(recipe)) return;
    navigate(`/hobby/cooking/${encodeURIComponent(recipe.id)}/edit`);
    const detail = await fetchRecipeDetail(recipe);
    if (!canEditAccess(detail)) {
      setSelectedRecipe(detail);
      setEditingRecipe(null);
      navigate(`/hobby/cooking/${encodeURIComponent(detail.id)}`, { replace: true });
      return;
    }
    setEditingRecipe({
      ...detail,
      title: detail.name,
      dietary: detail.dietaryList,
      tags: detail.tagList,
    });
  };

  useEffect(() => {
    if (isAddFlow || editingRecipe) {
      onRegisterCloseGuard?.(() => handleCancelForm({ confirmDiscard: true }));
      return () => onRegisterCloseGuard?.(null);
    }

    if (selectedRecipe) {
      onRegisterCloseGuard?.(() => navigate('/hobby/cooking'));
      return () => onRegisterCloseGuard?.(null);
    }

    onRegisterCloseGuard?.(null);
    return undefined;
  }, [editingRecipe, handleCancelForm, isAddFlow, navigate, onRegisterCloseGuard, selectedRecipe]);

  if (isAddFlow || editingRecipe) {
    return (
      <>
        <CookingForm
          onCancel={handleCancelForm}
          onSave={handleSave}
          isSaving={isSaving || isUpdating || isUploadingPhoto || isRemovingPhoto}
          initialData={editingRecipe || scrapedData}
          onDirtyChange={setHasFormChanges}
          recipeBooks={recipeBooks}
        />
        {renderPremiumUpsell()}
      </>
    );
  }

  if (isLoadingRouteRecipe || (routeRecipeId && !selectedRecipe && !editingRecipe)) {
    return (
      <div className="module-wrapper cooking-module">
        <div className="module-loading-state">
          <ModuleListSkeleton variant="single" />
        </div>
      </div>
    );
  }

  if (selectedRecipe) {
    const selectedAccessLevel = accessLevelFor(selectedRecipe);
    const detailFacts = [
      ['Total time', selectedRecipe.totalTime && `${selectedRecipe.totalTime} min`],
      ['Servings', selectedRecipe.servings && `${selectedRecipe.servings}`],
      ['Difficulty', selectedRecipe.difficulty],
      ['Rating', selectedRecipe.rating && <RatingReadout rating={selectedRecipe.rating} />],
    ].filter(([, value]) => Boolean(value));
    const organizationRows = [
      ['Status', [selectedRecipe.status]],
      ['Meal types', selectedRecipe.mealTypes],
      ['Categories', selectedRecipe.categories],
      ['Cuisine', [selectedRecipe.cuisine]],
      ['Dietary filters', selectedRecipe.dietaryList],
      ['Tags', selectedRecipe.tagList],
    ]
      .map(([label, values]) => [label, (values || []).filter(Boolean)])
      .filter(([, values]) => values.length > 0);

    return (
      <div className="module-wrapper cooking-module">
        {activeCookingSession && (
          <CookingSessionModal
            session={activeCookingSession}
            onSave={handleSaveCookingProgress}
            onFinish={handleFinishCooking}
            onDiscard={handleDiscardCooking}
            onClose={() => setActiveCookingSession(null)}
          />
        )}
        {showLogPastCook && (
          <div className="modal-overlay">
            <form className="modal-content cooking-log-past" onSubmit={handleLogPastCook}>
              <p className="eyebrow">Cooking history</p>
              <h2>Log a past cook</h2>
              <label className="form-group">Cooked on<input name="cookedDate" type="date" max={new Date().toISOString().slice(0, 10)} defaultValue={new Date().toISOString().slice(0, 10)} required /></label>
              <label className="form-group">What would you change next time?<textarea name="notes" rows="4" /></label>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowLogPastCook(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Save cook</button>
              </div>
            </form>
          </div>
        )}
        <div className="recipe-detail-view">
          <button type="button" className="recipe-back-link" onClick={() => navigate('/hobby/cooking')}>← Back</button>

          {sharingRecipe && (
            <ShareRecipeModal
              recipe={sharingRecipe}
              shares={shareData?.getRecipeShares || []}
              onClose={() => setSharingRecipe(null)}
              onShare={handleShareRecipe}
              onRevoke={handleRevokeShare}
              isSharing={isSharing}
              isRevoking={isRevoking}
              error={shareError}
              successMessage={shareSuccess}
            />
          )}

          {recipePendingDelete && (
            <div className="modal-overlay">
              <div className="modal-content delete-confirmation">
                <p className="eyebrow">Delete recipe</p>
                <h2>Delete recipe?</h2>
                <p>
                  This will permanently remove “{recipePendingDelete.name}” from your cookbook.
                  This action cannot be undone.
                </p>
                <div className="form-actions">
                  <button type="button" className="btn-ghost" onClick={() => setRecipePendingDelete(null)}>
                    Cancel
                  </button>
                  <button type="button" className="btn-danger solid" onClick={handleConfirmDelete} disabled={isDeleting}>
                    {isDeleting ? 'Deleting...' : 'Delete recipe'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <section className="recipe-detail-hero">
            <EntryImage
              className="entry-detail-photo"
              src={selectedRecipe.coverPhotoUrl}
              title={selectedRecipe.name}
              icon="🍽"
              fallbackType="cooking"
              variant="detail-square"
            />
            <div className="recipe-detail-copy">
              <p className="eyebrow">{selectedRecipe.recipeBook}</p>
              <div className="recipe-detail-title-row">
                <h2>{selectedRecipe.name}</h2>
              </div>
              {selectedRecipe.description && <p>{selectedRecipe.description}</p>}
              <button type="button" className="btn-primary cooking-start-button" onClick={handleStartCooking}>
                {selectedRecipe.cookingSummary?.activeSessionId ? 'Continue cooking' : 'Start cooking'}
              </button>
            </div>
            {detailFacts.length > 0 && (
              <div className="recipe-detail-facts">
                {detailFacts.map(([label, value]) => (
                  <div key={label}>
                    <span>{label}</span>
                    <strong>{value}</strong>
                  </div>
                ))}
              </div>
            )}
          </section>

          {selectedAccessLevel !== 'owner' && (
            <div className={`shared-access-banner ${selectedAccessLevel === 'edit' ? 'editable' : ''}`}>
              <strong>{sharedAccessLabel(selectedAccessLevel)}</strong>
              <span>{sharedByLabel(selectedRecipe.sharedByName)}</span>
            </div>
          )}

          <section className="recipe-detail-panel cooking-activity-panel">
            <button type="button" className="cooking-activity-row" onClick={() => setShowCookingHistory(true)}>
              <i aria-hidden="true">↻</i>
              <span>
                <strong>Cooking activity</strong>
                <small>
                  {Number(selectedRecipe.cookingSummary?.timesCooked || 0) > 0
                    ? `${selectedRecipe.cookingSummary.timesCooked} times made · Last made ${displayCookedDate(selectedRecipe.cookingSummary.lastCookedAt)}`
                    : 'No cooks logged yet'}
                </small>
              </span>
              <b aria-hidden="true">›</b>
            </button>
            {canEditAccess(selectedRecipe) && (
              <button type="button" className="detail-context-action" onClick={() => setShowLogPastCook(true)}>
                <span aria-hidden="true">＋</span> Log a past cook
              </button>
            )}
          </section>

          <div className="recipe-detail-grid">
            <section className="recipe-detail-panel">
              <h3>Ingredients</h3>
              <ul className="ingredient-list">
                {selectedRecipe.ingredients.map((ingredient, index) => (
                  <li key={`${ingredient.item}-${index}`}>
                    <strong>{[ingredient.quantity, ingredient.unit].filter(Boolean).join(' ')}</strong>
                    <span>{ingredient.item}</span>
                    {ingredient.note && <em>{ingredient.note}</em>}
                  </li>
                ))}
              </ul>
            </section>

            <section className="recipe-detail-panel">
              <h3>Instructions</h3>
              <ol className="instruction-list">
                {selectedRecipe.instructions.map((step, index) => (
                  <li key={`${step.text}-${index}`}>{step.text}</li>
                ))}
              </ol>
            </section>
          </div>

          <section className="recipe-detail-panel">
            <h3>Organization</h3>
            {organizationRows.length > 0 ? (
              <div className="recipe-organization-list">
                {organizationRows.map(([label, value]) => (
                  <div key={label}>
                    <span>{label}</span>
                    <div className="recipe-tags compact-tags">
                      {value.map((item) => <span key={item}>{item}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="detail-notes">No organization details yet.</p>
            )}
          </section>

          {selectedRecipe.notes && (
            <section className="recipe-detail-panel">
              <h3>Review</h3>
              <p className="detail-notes">{selectedRecipe.notes}</p>
            </section>
          )}

          {showCookingHistory && (
            <div className="modal-backdrop" onMouseDown={() => setShowCookingHistory(false)}>
              <section className="modal-card cooking-history-modal" role="dialog" aria-modal="true" aria-labelledby="cooking-history-title" onMouseDown={(event) => event.stopPropagation()}>
                <header>
                  <div><p className="eyebrow">Cooking activity</p><h2 id="cooking-history-title">Cooking history</h2></div>
                  <button type="button" className="btn-ghost" onClick={() => setShowCookingHistory(false)}>Done</button>
                </header>
                <div className="cooking-history-summary">
                  <div><strong>{selectedRecipe.cookingSummary?.timesCooked || 0}</strong><span>Times made</span></div>
                  <div><strong>{displayCookedDate(selectedRecipe.cookingSummary?.lastCookedAt)}</strong><span>Last made</span></div>
                </div>
                <div className="cooking-history-list">
                  {cookingHistoryLoading && cookingHistory.length === 0 && <div className="cooking-history-loading">Loading history...</div>}
                  {cookingHistory.map((session) => (
                    <div key={session.id}>
                      <span aria-hidden="true">✓</span>
                      <div><strong>{displayCookedDate(session.cookedDate || session.completedAt)}</strong>{Number(session.servingsMultiplier || 1) !== 1 && <small>{session.servingsMultiplier}x servings</small>}{session.notes && <p>{session.notes}</p>}</div>
                    </div>
                  ))}
                </div>
                {cookingHistoryData?.getRecipeCookingHistory?.pageInfo?.hasNextPage && (
                  <button type="button" className="btn-secondary cooking-history-more" onClick={handleLoadMoreCookingHistory} disabled={cookingHistoryLoading}>
                    {cookingHistoryLoading ? 'Loading...' : 'Load more'}
                  </button>
                )}
              </section>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="module-wrapper cooking-module">
      {renderPremiumUpsell()}
      {showImportModal && (
        <ImportModal
          onImport={(url) => {
            setImportUrl(url);
            importRecipe({ variables: { url } });
          }}
          onCancel={() => setShowImportModal(false)}
          isImporting={isImporting}
        />
      )}

      <div className="recipe-library-layout">
        <aside className="recipe-books-panel">
          <p className="eyebrow">Recipe books</p>
          <div className="recipe-book-list">
            <button
              type="button"
              className={!activeBook ? 'active' : ''}
              onClick={() => setActiveBook('')}
            >
              <span>All recipes</span>
              <strong>{parsedRecipes.length}</strong>
            </button>
            {recipeBooks.map((book) => (
              <button
                key={book}
                type="button"
                className={activeBook === book ? 'active' : ''}
                onClick={() => setActiveBook(book)}
              >
                <span>{book}</span>
                <strong>
                  {parsedRecipes.filter((recipe) => recipe.recipeBook === book).length}
                </strong>
              </button>
            ))}
          </div>
        </aside>

        <section className="recipe-main-panel">
          <div className="recipe-filters">
            <div className="form-group search-field">
              <label>Search recipes</label>
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by title, ingredient, cuisine, or tag"
              />
            </div>

            <div className="filter-row">
              <label>
                Meal type
              <select value={activeMealType} onChange={(event) => setActiveMealType(event.target.value)}>
                <option value="">All</option>
                {mealTypes.map((mealType) => <option key={mealType} value={mealType}>{mealType}</option>)}
              </select>
              </label>
              <label>
                Category
              <select value={activeCategory} onChange={(event) => setActiveCategory(event.target.value)}>
                <option value="">All</option>
                {categories.map((category) => <option key={category} value={category}>{category}</option>)}
              </select>
              </label>
              <label>
                Cuisine
              <select value={activeCuisine} onChange={(event) => setActiveCuisine(event.target.value)}>
                <option value="">All</option>
                {cuisines.map((cuisine) => <option key={cuisine} value={cuisine}>{cuisine}</option>)}
              </select>
              </label>
              <label>
                Dietary
              <select value={activeDiet} onChange={(event) => setActiveDiet(event.target.value)}>
                <option value="">All</option>
                {diets.map((diet) => <option key={diet} value={diet}>{diet}</option>)}
              </select>
              </label>
              <button
                type="button"
                className={`favorite-filter ${favoritesOnly ? 'active' : ''}`}
                onClick={() => setFavoritesOnly((current) => !current)}
              >
                Favorites
              </button>
            </div>
          </div>

          {loading && parsedRecipes.length === 0 && (
            <div className="module-loading-state">
              <ModuleListSkeleton variant="single" />
            </div>
          )}
          {error && <p className="error-copy">Error: {getPublicErrorMessage(error)}</p>}

          <div className="recipe-stats">
            <span>{filteredRecipes.length} shown</span>
            <span>{parsedRecipes.filter((recipe) => recipe.favorite).length} favorites</span>
            <span>{recipeBooks.length} books</span>
          </div>

          <div className="recipe-card-grid">
            {filteredRecipes.map((recipe) => (
              <button key={recipe.id} type="button" className="recipe-card has-entry-photo" onClick={() => handleSelectRecipe(recipe)}>
                <EntryImage src={recipe.coverPhotoUrl} title={recipe.name} icon="🍽" fallbackType="cooking" />
                <div>
                  <div className="movie-card-heading">
                    <h3>{recipe.name}</h3>
                    {recipe.favorite && <span className="favorite-chip">Favorite</span>}
                  </div>
                  <p>{recipe.recipeBook || 'Recipe book not added yet'}</p>
                  {[recipe.status, recipe.mealTypes[0], recipe.cookingSummary?.timesCooked > 0 && `Made ${recipe.cookingSummary.timesCooked} ${recipe.cookingSummary.timesCooked === 1 ? 'time' : 'times'}`].filter(Boolean).length > 0 && (
                    <p className="movie-card-detail-line">
                      {[recipe.status, recipe.mealTypes[0], recipe.cookingSummary?.timesCooked > 0 && `Made ${recipe.cookingSummary.timesCooked} ${recipe.cookingSummary.timesCooked === 1 ? 'time' : 'times'}`].filter(Boolean).join(' • ')}
                    </p>
                  )}
                  {recipe.accessLevel && recipe.accessLevel !== 'owner' && (
                    <p className="shared-card-line">{sharedAccessLabel(recipe.accessLevel)}</p>
                  )}
                </div>
              </button>
            ))}
          </div>

          {recipesPageInfo.hasNextPage && (
            <div className="load-more-row">
              <button type="button" className="btn-secondary" onClick={handleLoadMore} disabled={loading}>
                {loading ? 'Loading recipes...' : 'Load more recipes'}
              </button>
            </div>
          )}

          {filteredRecipes.length === 0 && !loading && (
            <HobbyEmptyState hobbyId="cooking" filtered={parsedRecipes.length > 0}
              title={parsedRecipes.length === 0 ? moduleCommonStrings.empty.nothingSaved : moduleCommonStrings.empty.noMatches}
              detail={parsedRecipes.length === 0 ? cookingStrings.empty.first : moduleCommonStrings.filters.tryDifferentSearchOrFilter} />
          )}
        </section>
      </div>
    </div>
  );
}
